import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
  ArrowRight, TrendingUp, Zap, BookOpen, Sparkles as SparklesIcon,
  ShoppingBag, UtensilsCrossed, Coffee, Sparkles,
  HardHat, Scissors, Shield, Building2,
} from 'lucide-react';

const INDUSTRIES = [
  { label: 'Restaurants',              icon: UtensilsCrossed },
  { label: 'Retail Stores',            icon: ShoppingBag },
  { label: 'Medical Clinics',          icon: Shield },
  { label: 'Salons and Spas',          icon: Scissors },
  { label: 'Construction Teams',       icon: HardHat },
  { label: 'Service-Based Businesses', icon: Building2 },
  { label: 'Coffee Shops',             icon: Coffee },
  { label: 'Cleaning Services',        icon: Sparkles },
];

export function HeroSection({ onQuickClock }) {
  const { t } = useTranslation();
  
  return (
    <section className="pt-10 pb-12 md:py-24 px-4" aria-label="Hero">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center">
          <div className="min-w-0 overflow-hidden">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-3 py-1 md:px-4 md:py-1.5 mb-4 md:mb-6">
              <TrendingUp size={14} className="text-orange-500" />
              <span className="text-[10px] md:text-xs font-semibold text-orange-700 uppercase tracking-wide">{t('landing.hero.badge')}</span>
            </div>
            <h1
              className="text-[34px] leading-[1.05] md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Manrope' }}
              data-testid="hero-headline"
            >
              {t('landing.hero.title')}
              <br />
              <span className="text-orange-500">{t('landing.hero.titleHighlight')}</span>
            </h1>
            <p className="mt-4 md:mt-6 text-base md:text-lg text-slate-600 leading-snug md:leading-relaxed" data-testid="hero-subheadline">
              {t('landing.hero.subtitle')}
            </p>
            <ul className="mt-4 md:mt-5 space-y-2 md:space-y-2.5">
              {[
                'Connect ChatGPT &amp; Claude over secure MCP',
                'Live labor cost dashboard + overtime alerts',
                'Payslips + QuickBooks / Xero exports',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-slate-700 text-sm font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>

            {/* AI trust badges — ChatGPT/Claude/OAuth/Tenant-isolated */}
            <div className="mt-4 md:mt-5 flex flex-wrap gap-1.5" data-testid="hero-ai-badges">
              {[
                'ChatGPT MCP',
                'Claude MCP',
                'OAuth Secure',
                'Tenant Isolated',
              ].map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1 text-[10px] md:text-[11px] font-semibold uppercase tracking-wide bg-slate-900 text-white rounded-full px-2.5 py-1"
                >
                  <SparklesIcon size={10} className="text-orange-300" />
                  {b}
                </span>
              ))}
            </div>
            
            {/* ── Exports to: QuickBooks + Xero logos ── */}
            <div className="mt-4 flex items-center gap-3 justify-center md:justify-start">
              <span className="text-xs text-slate-500 font-medium">Exports to:</span>
              <div className="flex items-center gap-3">
                <img 
                  src="https://restaff.b-cdn.net/logos/quickbook.png" 
                  alt="QuickBooks" 
                  className="h-[20px] md:h-[26px] opacity-60 hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
                <img 
                  src="https://restaff.b-cdn.net/logos/xero.png" 
                  alt="Xero" 
                  className="h-[20px] md:h-[26px] opacity-60 hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="w-full sm:w-auto">
                <Button size="lg" className="w-full bg-orange-500 hover:bg-orange-600 text-white text-base px-8 rounded-full shadow-md shadow-orange-200" data-testid="hero-cta-btn">
                  Start Free Trial
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="#try-demo" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full text-base rounded-full" data-testid="hero-see-how-btn">
                  Try Live Demo
                </Button>
              </a>
            </div>
            <div className="mt-2 md:mt-3 flex flex-col sm:flex-row gap-3" data-testid="hero-ai-ctas">
              <a href="#ai-operations" className="w-full sm:w-auto">
                <Button size="sm" variant="ghost" className="w-full text-sm rounded-full text-slate-700 hover:bg-slate-100" data-testid="hero-explore-ai-btn">
                  <SparklesIcon size={14} className="mr-1.5 text-orange-500" />
                  Explore AI Features
                </Button>
              </a>
              <Link to="/help/mcp" className="w-full sm:w-auto">
                <Button size="sm" variant="ghost" className="w-full text-sm rounded-full text-slate-700 hover:bg-slate-100" data-testid="hero-mcp-guide-btn">
                  <BookOpen size={14} className="mr-1.5 text-orange-500" />
                  MCP Setup Guide
                </Button>
              </Link>
            </div>
            <p className="mt-3 md:mt-4 text-xs md:text-sm text-slate-500 text-center md:text-left">
              30-day free trial · Cancel anytime
            </p>

            {/* ── Industry trust strip (desktop-only; removes mobile clutter) ── */}
            <div className="hidden md:block mt-5">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-3 text-center md:text-left">
                Built for businesses of all sizes, including
              </p>
              {/* Scrolling marquee */}
              <div className="relative overflow-hidden" style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)' }}>
                <div className="flex gap-2 industry-marquee" style={{ width: 'max-content' }}>
                  {[...INDUSTRIES, ...INDUSTRIES].map((ind, i) => {
                    const Icon = ind.icon;
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-xs font-medium whitespace-nowrap hover:bg-orange-50 hover:border-orange-200 hover:text-orange-700 transition-colors cursor-default"
                      >
                        <Icon size={12} className="flex-shrink-0" />
                        {ind.label}
                      </span>
                    );
                  })}
                </div>
                <style>{`
                  @keyframes industry-scroll {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                  }
                  .industry-marquee {
                    animation: industry-scroll 22s linear infinite;
                  }
                  .industry-marquee:hover {
                    animation-play-state: paused;
                  }
                `}</style>
              </div>
            </div>
            {/* Mobile Quick Clock */}
            <div className="mt-6 p-4 bg-slate-100 rounded-lg sm:hidden">
              <p className="text-sm text-slate-600 mb-2">Staff? Use Quick Clock:</p>
              <Button
                onClick={onQuickClock}
                variant="outline"
                className="w-full border-orange-200 text-orange-600"
              >
                <Zap className="mr-2" size={18} />
                Quick Clock In/Out
              </Button>
            </div>
          </div>

          {/* Hero image */}
          <div className="relative">
            <img
              src="https://restaff.b-cdn.net/Screenshots/dashoard.png"
              alt="ReStaff workforce management dashboard"
              className="rounded-2xl shadow-2xl w-full"
              loading="lazy"
              width="800"
              height="500"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
